<template>
  <div class="holiday-setting">
    <CCard>
      <CCardBody>
        <CRow class="mb-3 align-items-end">
          <CCol md="2">
            <label>{{ $t("holiday_setting.filter_year") }}</label>
            <b-form-select v-model="filters.year" :options="yearOptions" @change="search" />
          </CCol>
          <CCol md="4">
            <label>{{ $t("holiday_setting.filter_keyword") }}</label>
            <b-form-input v-model="filters.keyword" @keyup.enter="search" />
          </CCol>
          <CCol md="2">
            <CButton color="primary" @click="search">{{ $t("holiday_setting.search") }}</CButton>
          </CCol>
          <CCol md="4" class="text-right">
            <CButton color="success" @click="$refs.formModal.open()">
              {{ "+ " + $t("holiday_setting.create") }}
            </CButton>
          </CCol>
        </CRow>
        <div class="table-responsive">
          <table class="table table-mobile">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">{{ $t("holiday_setting.col_name") }}</th>
                <th scope="col">{{ $t("holiday_setting.col_description") }}</th>
                <th scope="col">{{ $t("holiday_setting.col_period") }}</th>
                <th scope="col" class="text-center">{{ $t("holiday_setting.col_days") }}</th>
                <th scope="col" class="text-center action-col">{{ $t("holiday_setting.col_action") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in holidaySettings" :key="item.id">
                <th scope="row">{{ (pagination.currentPage - 1) * pagination.perPage + index + 1 }}</th>
                <td>{{ item.title }}</td>
                <td>{{ item.description }}</td>
                <td>{{ formatPeriod(item) }}</td>
                <td class="text-center">{{ item.detail_count }}</td>
                <td class="text-center action-col">
                  <CButton color="primary" variant="outline" square size="sm" @click="$refs.formModal.open(item.id)">
                    {{ $t("button.label_action_edit") }}
                  </CButton>
                  <span class="mr-1"></span>
                  <CButton color="danger" variant="outline" square size="sm" @click="remove(item)">
                    {{ $t("button.label_action_delete") }}
                  </CButton>
                </td>
              </tr>
              <tr v-if="!holidaySettings.length">
                <td colspan="6" class="text-center">{{ $t("holiday_setting.no_data") }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCardBody>
      <CPagination
        v-if="pagination.totalPage > 1"
        :activePage="pagination.currentPage"
        :pages="pagination.totalPage"
        @update:activePage="fetchData"
        size="sm"
        align="center"
      />
    </CCard>
    <HolidaySettingFormModal ref="formModal" @saved="onSaved" />
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import HolidaySettingFormModal from "./HolidaySettingFormModal.vue";

// Holiday settings list: filter by year / name, create, edit, delete
export default {
  name: "ListHolidaySetting",
  components: { HolidaySettingFormModal },
  metaInfo() {
    return {
      title: this.$t("holiday_setting.title"),
      titleTemplate: "%s | " + this.$t("page_title.head_title")
    };
  },
  data() {
    return {
      filters: { year: moment().year(), keyword: "" }
    };
  },
  computed: {
    ...mapGetters({
      holidaySettings: "holiday-setting/holidaySettings",
      pagination: "holiday-setting/pagination"
    }),
    yearOptions() {
      const current = moment().year();
      const years = [];
      for (let year = current + 1; year >= current - 5; year--) {
        years.push({ value: year, text: String(year) });
      }
      return [{ value: "", text: "--" }, ...years];
    }
  },
  created() {
    this.fetchData(1);
  },
  methods: {
    ...mapActions("layout", ["setLoading"]),
    ...mapActions("holiday-setting", ["listHolidaySettings", "deleteHolidaySetting"]),
    async fetchData(page = 1) {
      this.setLoading(true);
      try {
        await this.listHolidaySettings({ ...this.filters, page });
      } catch (e) {
        // Error toast already shown by axios interceptor
      } finally {
        this.setLoading(false);
      }
    },
    search() {
      this.fetchData(1);
    },
    // After save: switch year filter to the saved holiday's year so it stays visible
    onSaved(year) {
      if (this.filters.year && year && this.filters.year !== year) {
        this.filters.year = year;
        this.fetchData(1);
        return;
      }
      this.fetchData(this.pagination.currentPage);
    },
    formatPeriod(item) {
      if (!item.start_date) return "";
      const start = moment(item.start_date).format("DD/MM/YYYY");
      const end = moment(item.end_date).format("DD/MM/YYYY");
      return start === end ? start : `${start} - ${end}`;
    },
    async remove(item) {
      const confirmed = await this.$confirm(
        `${this.$t("holiday_setting.delete_confirm")} "${item.title}"?`,
        this.$t("popup.label_popup_title"),
        this.$t("popup.label_confirm_button"),
        this.$t("popup.label_cancel_button"),
        "danger"
      );
      if (!confirmed) return;
      this.setLoading(true);
      try {
        await this.deleteHolidaySetting(item.id);
        this.$toast(this.$t("holiday_setting.delete_success"), null, 2000, "b-toaster-top-center", "success");
        const page = this.holidaySettings.length === 1 && this.pagination.currentPage > 1
          ? this.pagination.currentPage - 1 : this.pagination.currentPage;
        await this.fetchData(page);
      } catch (e) {
        // Error toast already shown by axios interceptor
      } finally {
        this.setLoading(false);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
label {
  font-weight: 600;
}
.action-col {
  width: 160px;
}
@media screen and (max-width: 767px) {
  .table-mobile {
    width: 900px;
  }
}
</style>
